import {
  FileText,
  Receipt,
  CreditCard,
  CalendarPlus,
  CalendarClock,
  FileImage,
  Users,
  LifeBuoy,
  Briefcase,
  TrendingUp,
  UsersRound,
  MessageSquare,
  Sparkles,
  Headset,
  Megaphone,
} from 'lucide-react';

export const features = [
  {
    slug: 'quoting',
    name: 'Quoting',
    icon: FileText,
    tagline: 'Turn an enquiry into a priced quote in minutes.',
    stat: { value: 'Minutes', label: 'from enquiry to a sent, priced quote' },
    description:
      'Build quotes from your existing product and service catalog, send them to customers over WhatsApp or email, and convert an accepted quote directly into an order — no retyping line items or prices.',
    points: [
      'Pull line items and pricing straight from your Products & Services catalog',
      'Send quotes over WhatsApp or email with one click',
      'Convert an accepted quote directly into an order or subscription',
    ],
    steps: [
      { title: 'Pull from your catalog', detail: 'Select products or services from your existing catalog — pricing and descriptions come in automatically.' },
      { title: 'Send it instantly', detail: 'Deliver the quote over WhatsApp or email straight from the platform, no separate document to attach.' },
      { title: 'Convert on acceptance', detail: 'The moment a customer accepts, the quote becomes an order or subscription with nothing re-typed.' },
    ],
    connectedModules: ['products', 'orders', 'subscriptions'],
    industries: ['ro-servicing', 'medical-equipment', 'civil-construction'],
  },
  {
    slug: 'invoicing',
    name: 'Invoicing',
    icon: Receipt,
    tagline: 'Invoices generated from completed work, automatically.',
    stat: { value: 'Zero', label: 'manual line-item tallying at month-end' },
    description:
      'Every completed order, service visit, or subscription delivery can generate an invoice automatically — with parts, labor, and recurring charges rolled up correctly, so nothing gets tallied by hand at month-end.',
    points: [
      'Auto-generate invoices from completed orders and service visits',
      'Recurring AMC and subscription billing consolidates monthly without manual math',
      'Full billing history tied to each customer record',
    ],
    steps: [
      { title: 'Work gets completed', detail: 'A technician closes out a job, delivery, or subscription visit in Tecnvi Ongo.' },
      { title: 'Invoice drafts itself', detail: 'Parts, labor, and any recurring charges roll up automatically against the right customer.' },
      { title: 'Send and track', detail: 'Send the invoice and track paid, partial, and outstanding status from the same record.' },
    ],
    connectedModules: ['orders', 'subscriptions', 'customers'],
    industries: ['ro-servicing', 'vending-machines', 'medical-equipment'],
  },
  {
    slug: 'payments',
    name: 'Payments',
    icon: CreditCard,
    tagline: 'Collect payment without chasing it down.',
    stat: { value: 'Automatic', label: 'payment reminders on outstanding balances' },
    description:
      'Record payments against any invoice, track what is outstanding per customer, and send payment links or reminders so collections stop depending on a phone call.',
    points: [
      'Track paid, partial, and outstanding balances per customer',
      'Send payment reminders and links over WhatsApp',
      'Full payment history alongside every order and subscription',
    ],
    steps: [
      { title: 'Invoice goes out', detail: 'An invoice is generated from a completed order, visit, or subscription cycle.' },
      { title: 'Customer gets reminded', detail: 'Payment reminders and links go out over WhatsApp without office staff chasing calls.' },
      { title: 'Balance reconciles', detail: 'Every payment is recorded against the invoice, so outstanding balances are always accurate.' },
    ],
    connectedModules: ['orders', 'subscriptions'],
    industries: ['ro-servicing', 'vending-machines'],
  },
  {
    slug: 'online-bookings',
    name: 'Online Bookings',
    icon: CalendarPlus,
    tagline: 'Let customers book themselves, day or night.',
    stat: { value: '24/7', label: 'booking availability, no office staff required' },
    description:
      'Customers book an appointment directly — by phone through the AI voice bot, over WhatsApp, or through Tecnvi Connect — against your real-time availability, with no office staff needed to take the call.',
    points: [
      'Real-time availability, no double-booking',
      'Booking via AI voice bot, WhatsApp, or Tecnvi Connect',
      'Automatic confirmation the moment a slot is booked',
    ],
    steps: [
      { title: 'Customer reaches out', detail: 'By phone, WhatsApp, or through Tecnvi Connect — whichever channel they prefer.' },
      { title: 'Real slot, booked instantly', detail: 'Availability is checked live, so there is never a double-booking.' },
      { title: 'Confirmation sent', detail: 'The customer gets an instant confirmation, no office callback needed.' },
    ],
    connectedModules: ['appointments'],
    industries: ['ac-fridge-repair', 'ro-servicing', 'medical-equipment'],
  },
  {
    slug: 'scheduling',
    name: 'Scheduling',
    icon: CalendarClock,
    tagline: 'The right technician, the right job, the right day.',
    stat: { value: 'One calendar', label: 'for the whole office and every technician' },
    description:
      'Set weekly availability, assign jobs to specific technicians based on location and workload, and keep every calendar — office and field — perfectly in sync.',
    points: [
      'Weekly availability templates with configurable slot durations',
      'Assign jobs by technician, location, or workload',
      'Automatic reminders before every scheduled visit',
    ],
    steps: [
      { title: 'Set availability once', detail: 'Weekly templates define your working hours and slot durations per technician.' },
      { title: 'Assign by fit', detail: 'Jobs get assigned by location and workload, not by whoever answers first.' },
      { title: 'Everyone stays synced', detail: 'Office and field calendars update together in real time.' },
    ],
    connectedModules: ['appointments', 'workforce'],
    industries: ['ac-fridge-repair', 'civil-construction', 'medical-equipment'],
  },
  {
    slug: 'field-documentation',
    name: 'Field Documentation',
    icon: FileImage,
    tagline: 'Proof of work, captured on-site, every time.',
    stat: { value: 'Photo + notes', label: 'attached to every completed job automatically' },
    description:
      'Technicians close out every job in Tecnvi Ongo with notes and photos attached directly to the job record — building a clean, timestamped audit trail without extra paperwork.',
    points: [
      'Photo and note capture attached to every completed job',
      'Timestamped record for compliance and warranty disputes',
      'Visible to the office in real time, not after the fact',
    ],
    steps: [
      { title: 'Technician closes the job', detail: 'From Tecnvi Ongo, a job is marked complete on-site.' },
      { title: 'Evidence attaches automatically', detail: 'Photos and notes are captured as part of closing out, not a separate step.' },
      { title: 'Office sees it live', detail: 'The record is visible back at the office immediately, building an audit trail as a byproduct of the work.' },
    ],
    connectedModules: ['workforce'],
    industries: ['civil-construction', 'medical-equipment', 'ac-fridge-repair'],
  },
  {
    slug: 'customer-management',
    name: 'Customer Management',
    icon: Users,
    tagline: 'One record per customer, not four.',
    stat: { value: '1 record', label: 'for every call, order, and service visit' },
    description:
      'Every call, order, appointment, and service visit is tied to a single customer record — separate pipelines for individual (B2C) and business (B2B) accounts, with full history in one place.',
    points: [
      'Separate B2C and B2B pipelines',
      'Full service and order history per customer',
      'Document storage for contracts and compliance paperwork',
    ],
    steps: [
      { title: 'Customer makes contact', detail: 'A call, WhatsApp message, or order automatically creates or links to their record.' },
      { title: 'History builds itself', detail: 'Every future order, visit, or conversation attaches to that same record.' },
      { title: 'Anyone can pick up context', detail: 'Office staff see full history instantly, no digging through separate systems.' },
    ],
    connectedModules: ['customers'],
    industries: ['medical-equipment', 'civil-construction', 'ro-servicing'],
  },
  {
    slug: 'customer-hub',
    name: 'Customer Hub',
    icon: LifeBuoy,
    tagline: 'A self-service home base for every customer.',
    stat: { value: 'Fewer calls', label: 'for routine order and service status checks' },
    description:
      'Tecnvi Connect gives customers a direct channel to check order status, view service history, and reach your team — without waiting on hold or calling the office.',
    points: [
      'Order and service status visible to the customer directly',
      'Direct self-service via WhatsApp, call, or web link',
      'Reduces inbound calls for routine status checks',
    ],
    steps: [
      { title: 'Customer opens Tecnvi Connect', detail: 'Via WhatsApp, a call, or a simple web link — no app download required.' },
      { title: 'Status is right there', detail: 'Order, appointment, and service history are visible without asking the office.' },
      { title: 'Escalate only if needed', detail: 'A real person is one message away for anything self-service can’t resolve.' },
    ],
    connectedModules: ['customers', 'orders'],
    industries: ['vending-machines', 'ac-fridge-repair'],
  },
  {
    slug: 'job-management',
    name: 'Job Management',
    icon: Briefcase,
    tagline: 'Every job tracked from assignment to sign-off.',
    stat: { value: 'Live status', label: 'from assignment through completion' },
    description:
      'Jobs move through a clear status lifecycle — accepted, in-progress, completed — visible to the office in real time via Tecnvi Ongo, with notes and photos attached at close-out.',
    points: [
      'Live status updates from the field',
      'Clear accountability per technician per job',
      'Completion notes and photos attached automatically',
    ],
    steps: [
      { title: 'Job gets assigned', detail: 'A technician receives the job with customer, location, and details attached.' },
      { title: 'Status updates live', detail: 'Accepted, in-progress, completed — the office sees it change in real time.' },
      { title: 'Sign-off is automatic', detail: 'Photos and notes attach at close-out, no separate report to file.' },
    ],
    connectedModules: ['workforce', 'orders'],
    industries: ['civil-construction', 'ac-fridge-repair', 'vending-machines'],
  },
  {
    slug: 'sales-pipeline',
    name: 'Sales Pipeline',
    icon: TrendingUp,
    tagline: 'From first enquiry to signed contract, tracked.',
    stat: { value: 'No lost leads', label: 'from call, WhatsApp, or web enquiries' },
    description:
      'Track leads and quotes as they move toward a signed job or AMC contract, so nothing generated by a call or WhatsApp enquiry falls through the cracks.',
    points: [
      'Leads created automatically from calls, WhatsApp, or web enquiries',
      'Track quotes and follow-ups through to a won contract',
      'Visibility into where every opportunity currently stands',
    ],
    steps: [
      { title: 'Enquiry comes in', detail: 'A call, WhatsApp message, or web form automatically creates a lead.' },
      { title: 'Quote and follow up', detail: 'Move the lead through quoting and follow-ups without losing track of it.' },
      { title: 'Win the contract', detail: 'A won lead converts into an order, job, or AMC subscription directly.' },
    ],
    connectedModules: ['customers', 'orders'],
    industries: ['medical-equipment', 'civil-construction'],
  },
  {
    slug: 'team-management',
    name: 'Team Management',
    icon: UsersRound,
    tagline: 'Your whole workforce, organized in one place.',
    stat: { value: 'Scoped access', label: 'each technician sees only their own jobs' },
    description:
      'Manage employee and freelancer profiles, departments, attendance, and performance — with field staff logging in through Tecnvi Ongo to see only what is assigned to them.',
    points: [
      'Employee and freelancer profiles with roles and departments',
      'Attendance and performance tracking without spreadsheets',
      'Field access scoped to each technician’s own assignments',
    ],
    steps: [
      { title: 'Add your team', detail: 'Employees and freelancers get profiles with roles, departments, and contact details.' },
      { title: 'Field access, scoped', detail: 'Each technician logs into Tecnvi Ongo and sees only their own assignments.' },
      { title: 'Performance tracks itself', detail: 'Attendance, completion rates, and ratings build up automatically, no spreadsheet needed.' },
    ],
    connectedModules: ['workforce'],
    industries: ['civil-construction', 'vending-machines', 'ac-fridge-repair'],
  },
  {
    slug: 'customer-communication',
    name: 'Customer Communication',
    icon: MessageSquare,
    tagline: 'Every conversation, on the channel your customer prefers.',
    stat: { value: 'One thread', label: 'across WhatsApp, SMS, and voice' },
    description:
      'Reach customers over WhatsApp, SMS, or a phone call for confirmations, reminders, and updates — all logged against their customer record, so context is never lost between channels.',
    points: [
      'WhatsApp, SMS, and voice — unified against one customer record',
      'Automatic confirmations and reminders for every booking',
      'No context lost between office staff and channels',
    ],
    steps: [
      { title: 'Message goes out', detail: 'A confirmation, reminder, or update is sent on whichever channel the customer prefers.' },
      { title: 'It logs automatically', detail: 'Every message is attached to that customer’s record, regardless of channel.' },
      { title: 'Anyone can continue it', detail: 'The next staff member sees the full conversation history, not a fresh start.' },
    ],
    connectedModules: ['customers', 'appointments'],
    industries: ['ro-servicing', 'ac-fridge-repair'],
  },
  {
    slug: 'ai-tools',
    name: 'AI Tools',
    icon: Sparkles,
    tagline: 'AI that handles the busywork, not just the buzzword.',
    stat: { value: 'Minutes', label: 'to turn a spreadsheet into a structured catalog' },
    description:
      'From an AI voice bot that books appointments over the phone to AI-assisted bulk catalog uploads that turn a spreadsheet or PDF into structured product data, AI removes manual work throughout the platform.',
    points: [
      'AI voice bot for inbound call handling and booking',
      'AI-assisted bulk upload for products and services catalogs',
      'Automatic text enhancement for catalog descriptions',
    ],
    steps: [
      { title: 'Upload what you have', detail: 'A spreadsheet, PDF, or even photos of your catalog or price list.' },
      { title: 'AI structures it', detail: 'Product names, prices, and categories get extracted and cleaned up automatically.' },
      { title: 'Ready to use immediately', detail: 'The structured catalog is live in Products & Services with no manual re-entry.' },
    ],
    connectedModules: ['products', 'services', 'appointments'],
    industries: ['ro-servicing', 'medical-equipment', 'vending-machines'],
  },
  {
    slug: 'receptionist',
    name: 'Receptionist',
    icon: Headset,
    tagline: 'A voice bot that never misses a call.',
    stat: { value: 'Every call', label: 'answered, even during peak hours' },
    description:
      'An AI receptionist answers inbound calls, books appointments, answers common questions, and routes to a real person when needed — so no call goes unanswered, even during peak hours.',
    points: [
      'Answers and routes every inbound call automatically',
      'Books appointments directly during the call',
      'Frees office staff from constant phone coverage',
    ],
    steps: [
      { title: 'Call comes in', detail: 'The AI receptionist answers immediately, no hold music, no missed calls.' },
      { title: 'Handles the request', detail: 'Books an appointment, answers a common question, or takes a message.' },
      { title: 'Routes when needed', detail: 'Anything beyond routine gets handed to a real person, with full context.' },
    ],
    connectedModules: ['appointments', 'customers'],
    industries: ['ac-fridge-repair', 'ro-servicing'],
  },
  {
    slug: 'marketing-tools',
    name: 'Marketing Tools',
    icon: Megaphone,
    tagline: 'Stay in front of customers between visits.',
    stat: { value: 'Targeted', label: 'by real service history, not guesswork' },
    description:
      'Send WhatsApp and email campaigns for AMC renewal reminders, seasonal offers, and re-engagement — targeted using your existing customer and service history.',
    points: [
      'WhatsApp and email marketing built on your customer data',
      'AMC renewal and re-engagement campaigns',
      'Targeted by service history and industry segment',
    ],
    steps: [
      { title: 'Segment by history', detail: 'Target customers by service history, AMC expiry, or industry segment.' },
      { title: 'Send the campaign', detail: 'WhatsApp or email campaigns go out directly from the platform.' },
      { title: 'Renewals come back in', detail: 'Responses convert straight back into bookings or renewed subscriptions.' },
    ],
    connectedModules: ['customers', 'subscriptions'],
    industries: ['ro-servicing', 'medical-equipment'],
  },
];

export const featureRows = [
  features.slice(0, 5),
  features.slice(5, 10),
  features.slice(10, 15),
];
